import React from 'react';

import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { reverse } from 'named-urls';

import ActionCell, { ActionConfig } from 'components/Grid/components/ActionCell';
import { ColumnActions } from 'components/Grid/types';

import { useStore } from 'utils/hooks/store';
import { canArchiveLibrary } from 'utils/roles/permissions';

import routes from 'core/routes';

import { ARCHIVE_CONFIRMATION_MESSAGE, HAZARD_FIELD } from '../../constants';
import { HazardRow } from '../../types';

type HazardActionCellProps = {
  rowData: HazardRow;
  actions: ColumnActions;
};

const HazardActionCell: React.FunctionComponent<HazardActionCellProps> = ({ rowData, actions: { refreshData } }) => {
  const { uuid, archivedAt } = rowData;
  const recordName = rowData[HAZARD_FIELD.name];
  const navigate = useNavigate();
  const { notification, session, hazardDetails } = useStore();

  const canArchive = canArchiveLibrary(session.user) && !session.user?.readOnly;
  const isArchived = Boolean(archivedAt);

  const setArchived = async (archived: boolean): Promise<void> => {
    if (await hazardDetails.archiveHazard(uuid, archived)) {
      await refreshData();
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (await hazardDetails.delete(uuid)) {
      notification.enqueueSuccessSnackbar('Hazard deleted successfully');
      await refreshData();
    } else if (hazardDetails.deleteBlockedReason) {
      notification.enqueueErrorSnackbar('This hazard is in use, so it cannot be deleted. Archive it instead.');
    } else {
      notification.enqueueErrorSnackbar('The hazard could not be deleted. Something went wrong.');
    }
  };

  const actions: ActionConfig[] = [
    {
      id: 'open-hazard',
      label: 'Open',
      onClick: () => navigate(reverse(routes.dashboard.risks.hazardDetails, { entityId: uuid })),
    },
    {
      id: 'archive-hazard',
      label: 'Archive',
      onClick: () => setArchived(true),
      visible: canArchive && !isArchived,
      confirmation: {
        type: 'simple',
        title: 'Archive hazard',
        message: ARCHIVE_CONFIRMATION_MESSAGE,
        confirmButtonLabel: 'Archive',
        hideRecoveryWarning: true,
      },
    },
    {
      id: 'unarchive-hazard',
      label: 'Unarchive',
      onClick: () => setArchived(false),
      visible: canArchive && isArchived,
    },
    {
      id: 'delete-hazard',
      label: 'Delete',
      onClick: handleDelete,
      visible: canArchive,
      confirmation: {
        type: 'simple',
        title: 'Delete hazard',
        message: 'Are you sure you want to delete this hazard?',
        confirmButtonLabel: 'Delete',
      },
    },
  ];

  return <ActionCell actions={actions} recordName={typeof recordName === 'string' ? recordName : undefined} />;
};

export default observer(HazardActionCell);
